import React, { useEffect, useRef } from "react";
import { useEntryScroll } from "@/lib/entryState";
import { Routes, Route, Navigate, Link, useNavigate } from "react-router-dom";
import { RoleProvider } from "./context/RoleContext";
import { NavHistoryProvider } from "./context/NavHistoryContext";
import { CommandPaletteProvider } from "./context/CommandPaletteContext";
import CommandPalette from "./components/CommandPalette";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import SchedulePage from "./pages/SchedulePage";
import DispatchPage from "./pages/DispatchPage";
import InstallersPage from "./pages/InstallersPage";
import MapPage from "./pages/MapPage";
import InstallPage from "./pages/InstallPage";
import TimeClockPage from "./pages/TimeClockPage";
import TimesheetsPage from "./pages/TimesheetsPage";
import DrawingsPage from "./pages/DrawingsPage";
import OnsiteMaterialPage from "./pages/OnsiteMaterialPage";
import WarehousePage from "./pages/WarehousePage";

// Landing on the bare "/" sends you to the dashboard. Checked in an effect against
// the live URL: entering Field Operations by clicking a sub-tab mounts this shell in
// the same commit as the push, and a plain <Navigate> would clobber that push.
function RootRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    if (window.location.pathname === "/") navigate("/dashboard", { replace: true });
  }, [navigate]);
  return null;
}

function NotFound() {
  return (
    <div className="p-10 text-center" data-testid="not-found-page">
      <div className="text-2xl font-semibold text-gray-900">Page not found</div>
      <p className="mt-2 text-sm text-gray-500">That address doesn’t exist in Field Operations.</p>
      <Link to="/dashboard" className="mt-4 inline-block text-sm text-blue-600 hover:underline"
            data-testid="not-found-home-link">Back to the dashboard</Link>
    </div>
  );
}

// Desk pages sit in the host's standard page body (even gutters, own scroll); the phone-first
// views keep their own edge-to-edge layout and only get a scroll box.
// Each page's scroll position belongs to its history entry, so Back lands where you left it.
const Desk = ({ children }) => { const ref = useRef(null); useEntryScroll(ref); return <div ref={ref} className="page-body">{children}</div>; };
const Bleed = ({ children }) => { const ref = useRef(null); useEntryScroll(ref); return <div ref={ref} className="page-bleed">{children}</div>; };

// The FieldOps shell, faithful to the source App.js. Host differences only:
// MemoryRouter (the host owns the URL), host auth (no AuthGate/LoginPage),
// role from the host account (RoleProvider reads useAuth). The page title and the
// sync chip live in the host header (PageHeader) so Field Ops matches every other tab.
export default function FieldOpsApp() {
  return (
    <RoleProvider>
        <NavHistoryProvider>
          <CommandPaletteProvider>
            <div className="fo-scope flex-1 min-h-0 flex bg-[#F8FAFC] text-slate-900" data-testid="fieldops-app">
              {/* Sub-nav lives in the host sidebar (under Field Operations) — no second panel. */}
              <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                <main className="flex-1 min-w-0 min-h-0 flex flex-col">
                  <Routes>
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/dashboard" element={<Desk><Dashboard /></Desk>} />
                    <Route path="/projects" element={<Desk><Projects /></Desk>} />
                    <Route path="/projects/:id" element={<Desk><ProjectDetail /></Desk>} />
                    <Route path="/mobilizations" element={<Navigate to="/schedule" replace />} />
                    <Route path="/schedule" element={<Desk><SchedulePage /></Desk>} />
                    <Route path="/dispatch" element={<Desk><DispatchPage /></Desk>} />
                    <Route path="/installers" element={<Desk><InstallersPage /></Desk>} />
                    <Route path="/map" element={<Bleed><MapPage /></Bleed>} />
                    <Route path="/install" element={<Bleed><InstallPage /></Bleed>} />
                    <Route path="/timeclock" element={<Bleed><TimeClockPage /></Bleed>} />
                    <Route path="/timesheets" element={<Desk><TimesheetsPage /></Desk>} />
                    <Route path="/drawings" element={<Bleed><DrawingsPage /></Bleed>} />
                    <Route path="/install/onsite" element={<Bleed><OnsiteMaterialPage /></Bleed>} />
                    <Route path="/warehouse" element={<Bleed><WarehousePage /></Bleed>} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </main>
              </div>
            </div>
            <CommandPalette />
          </CommandPaletteProvider>
        </NavHistoryProvider>
    </RoleProvider>
  );
}
